// EP-136 T136-024 — the "Cancel booking" modal behind UnitBookingCard.
// Renders one of two shapes depending on what there is to reverse:
//
//   - deposit mode with money actually collected: a bespoke Modal with a
//     forfeit/refund disposition choice (there's a real decision to make
//     about the paid booking fee).
//   - revenue mode (paid or not) OR an unpaid booking: a plain
//     ConfirmDialog. Revenue is non-refundable by definition; an unpaid
//     booking has collected nothing — neither case has a disposition to
//     choose.
//
// Submits via the existing useCancelBooking hook (POST /api/leases/:id/
// cancel_booking). `disposition` is omitted for the ConfirmDialog variants
// so the server applies the property's resolved default.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Button, Alert, Radio, ConfirmDialog } from '@/components/ui'
import { useCancelBooking } from '@/hooks/useLeases'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'

export interface CancelBookingModalProps {
  isOpen: boolean
  onClose: () => void
  leaseId: number
  /** Whether the booking invoice has actually been paid. */
  invoicePaid: boolean
  /** The resolved (property-override-over-account-default) booking fee mode. */
  bookingFeeMode: 'deposit' | 'revenue'
  /** The resolved forfeit-on-cancel default, used to pre-select the radio. */
  forfeitOnCancelDefault: boolean
}

type Disposition = 'forfeit' | 'refund'

export function CancelBookingModal({
  isOpen,
  onClose,
  leaseId,
  invoicePaid,
  bookingFeeMode,
  forfeitOnCancelDefault,
}: CancelBookingModalProps) {
  const { t } = useTranslation('leases')
  const { t: tc } = useTranslation('common')
  const translateError = useApiErrorTranslator('leases')
  const addToast = useNotificationStore((s) => s.addToast)
  const cancelBooking = useCancelBooking()

  const showDispositionChoice = invoicePaid && bookingFeeMode === 'deposit'

  const [disposition, setDisposition] = useState<Disposition>(
    forfeitOnCancelDefault ? 'forfeit' : 'refund',
  )

  // Re-seed the choice every time the modal opens, so a prior cancel
  // attempt on a different booking (or a changed property default) never
  // leaks into the next open. forfeitOnCancelDefault is a boolean primitive,
  // not an object reference, so this can't fall into the "resets forever"
  // trap of depending on a fresh object per render.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the disposition choice to the property default each time the modal (re)opens
    if (isOpen) setDisposition(forfeitOnCancelDefault ? 'forfeit' : 'refund')
  }, [isOpen, forfeitOnCancelDefault])

  const handleConfirm = async () => {
    try {
      await cancelBooking.mutateAsync({
        leaseId,
        ...(showDispositionChoice ? { disposition } : {}),
      })
      addToast({ type: 'success', message: t('booking.cancel.success') })
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (!showDispositionChoice) {
    return (
      <ConfirmDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirm}
        title={t('booking.cancel.title')}
        message={invoicePaid ? t('booking.cancel.revenue_message') : t('booking.cancel.unpaid_message')}
        confirmLabel={t('booking.cancel.action')}
        variant="error"
        loading={cancelBooking.isPending}
      />
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('booking.cancel.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={cancelBooking.isPending}>
            {tc('actions.cancel')}
          </Button>
          <Button variant="error" onClick={handleConfirm} loading={cancelBooking.isPending}>
            {t('booking.cancel.action')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">{t('booking.cancel.deposit_warning')}</Alert>

        <div className="space-y-2">
          <label
            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
              disposition === 'forfeit' ? 'border-primary bg-primary/5' : 'border-base-300'
            }`}
          >
            <Radio
              name="booking-cancel-disposition"
              checked={disposition === 'forfeit'}
              onChange={() => setDisposition('forfeit')}
              className="mt-0.5"
            />
            <span>
              <span className="block font-medium">{t('booking.cancel.forfeit_label')}</span>
              <span className="block text-sm text-base-content/60">
                {t('booking.cancel.forfeit_hint')}
              </span>
            </span>
          </label>

          <label
            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
              disposition === 'refund' ? 'border-primary bg-primary/5' : 'border-base-300'
            }`}
          >
            <Radio
              name="booking-cancel-disposition"
              checked={disposition === 'refund'}
              onChange={() => setDisposition('refund')}
              className="mt-0.5"
            />
            <span>
              <span className="block font-medium">{t('booking.cancel.refund_label')}</span>
              <span className="block text-sm text-base-content/60">
                {t('booking.cancel.refund_hint')}
              </span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  )
}
